import {
  Activity,
  ArrowRight,
  Building2,
  CheckCircle2,
  Church,
  CircleAlert,
  Mail,
  Upload,
  UserCheck,
  UserPlus,
  type LucideIcon,
} from "lucide-react";

import type {
  PlatformAuditEvent,
} from "@/api/platform";

interface RecentActivityCardProps {
  activity: PlatformAuditEvent[];
}

interface ActivityStyle {
  icon: LucideIcon;
  iconClassName: string;
}

const defaultActivityStyle: ActivityStyle = {
  icon: Activity,
  iconClassName: "bg-slate-100 text-slate-600",
};

const activityStyles: Record<
  string,
  ActivityStyle
> = {
  PILOT_REQUEST_SUBMITTED: {
    icon: UserPlus,
    iconClassName: "bg-indigo-50 text-indigo-600",
  },
  PILOT_REQUEST_STATUS_CHANGED: {
    icon: CheckCircle2,
    iconClassName: "bg-sky-50 text-sky-600",
  },
  PILOT_REQUEST_APPROVED: {
    icon: UserCheck,
    iconClassName: "bg-emerald-50 text-emerald-600",
  },
  PILOT_REQUEST_DECLINED: {
    icon: CircleAlert,
    iconClassName: "bg-rose-50 text-rose-600",
  },
  PILOT_REQUEST_CONVERTED: {
    icon: Building2,
    iconClassName: "bg-violet-50 text-violet-600",
  },
  CHURCH_CREATED: {
    icon: Building2,
    iconClassName: "bg-indigo-50 text-indigo-600",
  },
  CHURCH_ACTIVATED: {
    icon: Church,
    iconClassName: "bg-emerald-50 text-emerald-600",
  },
  INVITATION_SENT: {
    icon: Mail,
    iconClassName: "bg-amber-50 text-amber-600",
  },
  INVITATION_ACCEPTED: {
    icon: UserCheck,
    iconClassName: "bg-sky-50 text-sky-600",
  },
  IMPORT_STARTED: {
    icon: Upload,
    iconClassName: "bg-violet-50 text-violet-600",
  },
  IMPORT_COMPLETED: {
    icon: Upload,
    iconClassName: "bg-emerald-50 text-emerald-600",
  },
  IMPORT_FAILED: {
    icon: CircleAlert,
    iconClassName: "bg-rose-50 text-rose-600",
  },
};

export function RecentActivityCard({
  activity,
}: RecentActivityCardProps) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-950">
            Recent activity
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Latest platform and pilot events.
          </p>
        </div>

        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
          <CheckCircle2 className="h-5 w-5" />
        </div>
      </div>

      {activity.length > 0 ? (
        <div className="mt-6 divide-y divide-slate-100">
          {activity.map((item) => (
            <ActivityItem
              key={item.id}
              activity={item}
            />
          ))}
        </div>
      ) : (
        <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center">
          <Activity className="mx-auto h-6 w-6 text-slate-400" />

          <p className="mt-3 text-sm font-semibold text-slate-900">
            No platform activity yet
          </p>

          <p className="mt-1 text-sm text-slate-500">
            New pilot requests and administrative actions will
            appear here.
          </p>
        </div>
      )}

      <button
        type="button"
        className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 transition hover:text-indigo-500"
      >
        View all activity
        <ArrowRight className="h-4 w-4" />
      </button>
    </section>
  );
}

interface ActivityItemProps {
  activity: PlatformAuditEvent;
}

function ActivityItem({
  activity,
}: ActivityItemProps) {
  const style =
    activityStyles[activity.event_type] ??
    defaultActivityStyle;

  const Icon = style.icon;

  return (
    <article className="flex gap-4 py-4 first:pt-0 last:pb-0">
      <div
        className={[
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
          style.iconClassName,
        ].join(" ")}
      >
        <Icon className="h-5 w-5" />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold leading-5 text-slate-900">
          {buildActivityTitle(activity)}
        </p>

        <p className="mt-1 text-sm leading-5 text-slate-500">
          {buildActivityDescription(activity)}
        </p>

        <p className="mt-2 text-xs font-medium text-slate-400">
          {formatRelativeDate(activity.created_at)}
        </p>
      </div>
    </article>
  );
}

function buildActivityTitle(
  activity: PlatformAuditEvent
): string {
  const churchName = readEventDataString(
    activity.event_data,
    "church_name"
  );

  switch (activity.event_type) {
    case "PILOT_REQUEST_SUBMITTED":
      return churchName
        ? `Pilot request submitted by ${churchName}`
        : "New pilot request submitted";

    case "PILOT_REQUEST_STATUS_CHANGED":
      return churchName
        ? `Pilot request updated for ${churchName}`
        : "Pilot request status updated";

    case "PILOT_REQUEST_APPROVED":
      return churchName
        ? `${churchName} was approved`
        : "Pilot request approved";

    case "PILOT_REQUEST_DECLINED":
      return churchName
        ? `${churchName} was declined`
        : "Pilot request declined";

    case "PILOT_REQUEST_CONVERTED":
      return churchName
        ? `${churchName} became a pilot church`
        : "Pilot request converted";

    case "CHURCH_CREATED":
      return churchName
        ? `${churchName} was created`
        : "Pilot church created";

    case "CHURCH_ACTIVATED":
      return churchName
        ? `${churchName} was activated`
        : "Pilot church activated";

    case "INVITATION_SENT":
      return "Administrator invitation sent";

    case "INVITATION_ACCEPTED":
      return "Administrator invitation accepted";

    case "IMPORT_STARTED":
      return "Member import started";

    case "IMPORT_COMPLETED":
      return "Member import completed";

    case "IMPORT_FAILED":
      return "Member import failed";

    default:
      return formatEventType(activity.event_type);
  }
}

function buildActivityDescription(
  activity: PlatformAuditEvent
): string {
  const description =
    readEventDataString(
      activity.event_data,
      "description"
    ) ??
    readEventDataString(
      activity.event_data,
      "message"
    );

  if (description) {
    return description;
  }

  const previousStatus = readEventDataString(
    activity.event_data,
    "previous_status"
  );

  const newStatus = readEventDataString(
    activity.event_data,
    "new_status"
  );

  if (previousStatus && newStatus) {
    return `Status changed from ${formatStatus(
      previousStatus
    )} to ${formatStatus(newStatus)}.`;
  }

  const contactEmail = readEventDataString(
    activity.event_data,
    "contact_email"
  );

  if (contactEmail) {
    return `Primary contact: ${contactEmail}`;
  }

  const entityName = formatEventType(
    activity.entity_type
  );

  if (activity.actor_type === "SYSTEM") {
    return `Recorded automatically for this ${entityName.toLowerCase()}.`;
  }

  return `Recorded by a ${activity.actor_type.toLowerCase()} actor.`;
}

function readEventDataString(
  eventData: Record<string, unknown> | null | undefined,
  key: string
): string | null {
  const value = eventData?.[key];

  if (
    typeof value === "string" &&
    value.trim().length > 0
  ) {
    return value;
  }

  return null;
}

function formatEventType(value: string): string {
  return value
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map(
      (part) =>
        part.charAt(0).toUpperCase() +
        part.slice(1)
    )
    .join(" ");
}

function formatStatus(value: string): string {
  return value
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .join(" ");
}

function formatRelativeDate(value: string): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Unknown time";
  }

  const elapsedMilliseconds =
    Date.now() - date.getTime();

  const elapsedMinutes = Math.floor(
    elapsedMilliseconds / 60_000
  );

  if (elapsedMinutes < 1) {
    return "Just now";
  }

  if (elapsedMinutes < 60) {
    return `${elapsedMinutes} ${
      elapsedMinutes === 1 ? "minute" : "minutes"
    } ago`;
  }

  const elapsedHours = Math.floor(
    elapsedMinutes / 60
  );

  if (elapsedHours < 24) {
    return `${elapsedHours} ${
      elapsedHours === 1 ? "hour" : "hours"
    } ago`;
  }

  const elapsedDays = Math.floor(
    elapsedHours / 24
  );

  if (elapsedDays < 7) {
    return `${elapsedDays} ${
      elapsedDays === 1 ? "day" : "days"
    } ago`;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year:
      date.getFullYear() !==
      new Date().getFullYear()
        ? "numeric"
        : undefined,
  }).format(date);
}